import { ContentChannel, type Post } from '@mg/shared';

/** How much of the platform's own reason the dialog repeats before it stops being readable. */
const REASON_MAX = 160;

/**
 * The wording of the publish confirmation, so the All Posts views and the campaign page cannot
 * drift apart on it.
 *
 * A retry is a different question from a first publish and deserves different words: the user
 * clicked "Retry publish", and answering a dialog headed "Publish now" reads like the wrong
 * button. Repeating what the platform said last time is the part that makes the dialog worth
 * showing at all — "credits depleted" or "the token has expired" is usually something to go and
 * fix rather than retry into.
 */
export function publishConfirm(post: Pick<Post, 'title' | 'channel' | 'publishError'>): {
  title: string;
  message: string;
  confirmLabel: string;
} {
  const name = post.title || 'Untitled post';
  const destination =
    post.channel === ContentChannel.SOCIAL
      ? 'This posts it live to your connected social account.'
      : 'This notifies your n8n workflow.';

  if (post.publishError) {
    const reason = post.publishError.trim().slice(0, REASON_MAX);
    return {
      title: 'Retry publish',
      message: `Try publishing “${name}” again? It failed last time with: ${reason}\n\n${destination}`,
      confirmLabel: 'Retry',
    };
  }

  return {
    title: 'Publish now',
    message: `Publish “${name}” now? ${destination}`,
    confirmLabel: 'Publish',
  };
}
